import React from 'react';
import {Route} from 'react-router';
import {useHistory, useLocation, BrowserRouter} from 'react-router-dom';
import wrap from 'wraptile';
import {History} from 'history';
import MaterialBreadcrumbs from '@material-ui/core/Breadcrumbs';
import Typography from '@material-ui/core/Typography';
import Tooltip from '@material-ui/core/Tooltip';
import {
    MetadataGeneral,
    QCToolsMetadataState,
} from 'QCTools/QCToolsMetadata/QCToolsMetadataTypes';
import {ContentItemViewMetadataState} from 'ContentItemView/ContentItemViewMetadata/ContentItemViewMetadataTypes';
import {
    IngestDataState,
    IngestReportState,
    InitialEditingIngestConfigState,
} from 'Ingest/IngestData/IngestDataTypes';
import {UserState} from '@pluto/users';
import {
    QC_TOOLS,
    INGEST_DATA,
    ADD_USERS,
    MAIN_PAGE,
    USER_DETAILS,
    EDIT_INGEST_CONFIG,
    INGEST_REPORT,
    INGEST_SESSION_ERROR,
    SEARCH_ITEM,
} from 'App/AppRouter/roleNames';
import {TableRowType} from 'QCWorkflow/DeliveryManager/DeliveryManagerTypes';
import {ContentItemDataState} from './BreadcrumbsConnected';
import {BreadcrumbsProps, Crumbs} from './BreadcrumbsTypes';
import './Breadcrumbs.scss';

export const breadcrumbClick = wrap((history: History, to: string) => {
    const searchParams = sessionStorage.getItem('rootQuery') || '';
    return history.push(`${to}?${searchParams}`);
});

export const getQcToolsTitle = (general: MetadataGeneral | TableRowType) => {
    if (general.seriesTitle) {
        return `${general.seriesTitle} - S${general.seasonNumber} - E${general.episodeNumber}`;
    }
    return general?.title || '';
};

export const getBreadcrumbTitle = (
    name: string,
    lastPathName: string,
    qcToolsMetadata: QCToolsMetadataState,
    ingestData: IngestDataState,
    userDetails: UserState,
    initialValuesForEditingIngestConfig: InitialEditingIngestConfigState,
    ingestReport: IngestReportState,
    contentItemData: ContentItemDataState,
    contentItemViewMetadata: ContentItemViewMetadataState
) => {
    switch (name) {
        case QC_TOOLS:
            return getQcToolsTitle(qcToolsMetadata.general);

        case INGEST_DATA:
            return ingestData?.title || '';

        case ADD_USERS:
            return '';

        case USER_DETAILS:
            return userDetails?.name || '';

        case EDIT_INGEST_CONFIG:
            return initialValuesForEditingIngestConfig?.name || '';

        case INGEST_REPORT:
            return ingestReport?.title || '';

        case INGEST_SESSION_ERROR:
            return ingestData?.title || '';

        case SEARCH_ITEM:
            return contentItemViewMetadata.general?.displayTitle || '';

        default:
            return lastPathName;
    }
};

export const getFullBreadcrumbName = (name: string, title: string) =>
    `${name} ${title ? '-' : ''} ${title}`;

export const getBreadcrumbName = (
    name: string,
    pathNames: string[],
    qcToolsMetadata: QCToolsMetadataState,
    ingestData: IngestDataState,
    userDetails: UserState,
    initialValuesForEditingIngestConfig: InitialEditingIngestConfigState,
    ingestReport: IngestReportState,
    contentItemData: ContentItemDataState,
    contentItemViewMetadata: ContentItemViewMetadataState
) => {
    const lastPathName = pathNames[pathNames.length - 1];
    const title = getBreadcrumbTitle(
        name,
        lastPathName,
        qcToolsMetadata,
        ingestData,
        userDetails,
        initialValuesForEditingIngestConfig,
        ingestReport,
        contentItemData,
        contentItemViewMetadata
    );
    const lastBreadcrumbName = name === MAIN_PAGE ? '' : name;
    const fullBreadcrumbName = getFullBreadcrumbName(name, title);

    if (pathNames.length < 2) {
        return lastBreadcrumbName;
    }

    return fullBreadcrumbName;
};

export const mapPathNames = wrap(
    (
        history: History,
        pathNames: string[],
        crumbs: Crumbs[],
        qcToolsMetadata: QCToolsMetadataState,
        ingestData: IngestDataState,
        userDetails: UserState,
        initialValuesForEditingIngestConfig: InitialEditingIngestConfigState,
        ingestReport: IngestReportState,
        contentItemData: ContentItemDataState,
        contentItemViewMetadata: ContentItemViewMetadataState,
        crumb: Crumbs,
        index: number
    ) => {
        const last = index === crumbs.length - 1;
        const {name, route} = crumb;
        const breadcrumbName = getBreadcrumbName(
            name,
            pathNames,
            qcToolsMetadata,
            ingestData,
            userDetails,
            initialValuesForEditingIngestConfig,
            ingestReport,
            contentItemData,
            contentItemViewMetadata
        );

        return last ? (
            <Tooltip title={breadcrumbName} key={route}>
                <Typography color="textPrimary" className="breadcrumbs__tail">
                    {breadcrumbName}
                </Typography>
            </Tooltip>
        ) : (
            <span role="link" key={route} onClick={breadcrumbClick(history, route)}>
                {name}
            </span>
        );
    }
);

export const filterPathNames = (value: string) => Boolean(value);

export const Breadcrumbs = (props: BreadcrumbsProps) => {
    const {
        crumbs,
        qcToolsMetadata,
        ingestData,
        userDetails,
        initialValuesForEditingIngestConfig,
        ingestReport,
        contentItemData,
        contentItemViewMetadata,
    } = props;

    const history = useHistory();
    const location = useLocation();
    const pathNames = location.pathname.split('/').filter(filterPathNames);

    return (
        <BrowserRouter>
            <Route>
                <MaterialBreadcrumbs
                    aria-label="Breadcrumb"
                    className="breadcrumbs"
                    separator={<span className="separator">&#8594;</span>}
                >
                    {crumbs.map(
                        mapPathNames(
                            history,
                            pathNames,
                            crumbs,
                            qcToolsMetadata,
                            ingestData,
                            userDetails,
                            initialValuesForEditingIngestConfig,
                            ingestReport,
                            contentItemData,
                            contentItemViewMetadata
                        )
                    )}
                </MaterialBreadcrumbs>
            </Route>
        </BrowserRouter>
    );
};
